import React, { useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";
import { compressImage, errorText, imgSrc } from "./imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 12;

const ImageListEditor = ({ images, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - images.length);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    setUploading(files.length);
    const urls = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", await compressImage(f));
        const r = await axios.post(`${API}/admin/products/image`, fd, { withCredentials: true });
        urls.push(r.data.url);
      } catch (err) {
        toast.error(`${f.name}: ${errorText(err, "Upload fehlgeschlagen.")}`);
      }
      setUploading((n) => n - 1);
    }
    if (urls.length) {
      onChange((prev) => [...prev, ...urls]);
      toast.success(`${urls.length} Bild${urls.length > 1 ? "er" : ""} hochgeladen.`);
    }
  };

  const removeAt = (i) => onChange((prev) => prev.filter((_, idx) => idx !== i));
  const makeMain = (i) => onChange((prev) => [prev[i], ...prev.filter((_, idx) => idx !== i)]);

  return (
    <div data-testid="product-form-images">
      <label className="text-sm font-bold text-slate-600">Produktbilder ({images.length}/{MAX}) – das erste ist das Hauptbild</label>
      <div className="mt-2 flex flex-wrap gap-3">
        {images.map((u, i) => (
          <div key={u} className="relative group" data-testid={`product-form-image-${i}`}>
            <img src={imgSrc(u)} alt={`Bild ${i + 1}`}
              className={`h-24 w-24 rounded-xl object-cover border-2 ${i === 0 ? "border-[#f7941e]" : "border-slate-200"}`} />
            {i === 0 ? (
              <span className="absolute bottom-1 left-1 rounded-full bg-[#f7941e] px-2 text-[10px] font-extrabold text-white">Haupt</span>
            ) : (
              <button type="button" onClick={() => makeMain(i)} title="Als Hauptbild" data-testid={`product-form-image-main-${i}`}
                className="absolute bottom-1 left-1 h-7 w-7 rounded-full bg-white/90 text-[#f7941e] flex items-center justify-center shadow">
                <Star className="h-4 w-4" />
              </button>
            )}
            <button type="button" onClick={() => removeAt(i)} title="Entfernen" data-testid={`product-form-image-remove-${i}`}
              className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {images.length < MAX && (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading > 0}
            data-testid="product-form-image-upload"
            className="h-24 w-24 rounded-xl border-2 border-dashed border-sky-300 text-[#1273a8] flex flex-col items-center justify-center gap-1 text-xs font-bold hover:bg-sky-50 disabled:opacity-60">
            {uploading > 0 ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
            {uploading > 0 ? `${uploading} lädt…` : "Bilder hinzufügen"}
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={upload}
        className="hidden" data-testid="product-form-image-input" />
      <p className="text-xs text-slate-400 mt-2">Mehrere Bilder gleichzeitig auswählbar · PNG, JPG, WEBP · werden automatisch verkleinert</p>
    </div>
  );
};

export default ImageListEditor;
